import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactFlow, {
  Controls,
  MiniMap,
  Background,
  applyNodeChanges,
  applyEdgeChanges,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
  useReactFlow,
} from "reactflow";
import "reactflow/dist/style.css";
import { Box, Button, Alert, CircularProgress, Typography, Tooltip } from "@mui/material";
import Add from "@mui/icons-material/Add";
import { useChatbotSlots, useChatbotMutations } from "../../hooks/useChatbot";
import { slotsToFlow } from "./chatbotFlowLayout";
import { computeDefaultCollapsedIds } from "./chatbotFlowCollapse";
import { LARGE_TREE_THRESHOLD } from "./chatbotTreeModel";
import ChatbotNode from "./ChatbotNode";
import ChatbotNodePanel from "./ChatbotNodePanel";
import { isLinkButton, chatbotButtonFlowKey } from "../../utils/chatbotButtonLinks";
import {
  hasLocalExceptionForParent,
  canDeleteSlot,
} from "./chatbotSlotParentUtils";
import { getAncestorSlotIds } from "../../utils/chatbotFlowPathNavigation";

const nodeTypes = { chatbot: ChatbotNode };

function ChatbotFlowEditorInner({ userId, onEmptyInit, focusSlotRequest, simulatorActiveSlotId, onNavigateToSlot }) {
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [largeFlowWarningDismissed, setLargeFlowWarningDismissed] = useState(false);
  const [collapsedIds, setCollapsedIds] = useState(() => new Set());
  const collapseInitializedRef = useRef(false);

  const { data: slots = [], isLoading, refetch } = useChatbotSlots(userId);
  const { createSlot, updateSlot, deleteSlot } = useChatbotMutations(userId);
  const { fitView } = useReactFlow();
  /** Evita fitView a cada refetch (salvar): só enquadra na 1ª carga do fluxo por bot (userId). */
  const viewportFitRef = useRef({ userId: null, didFit: false });

  useEffect(() => {
    collapseInitializedRef.current = false;
    setCollapsedIds(new Set());
    setLargeFlowWarningDismissed(false);
  }, [userId]);

  useEffect(() => {
    if (!slots.length || collapseInitializedRef.current) return;
    setCollapsedIds(computeDefaultCollapsedIds(slots));
    collapseInitializedRef.current = true;
  }, [slots]);

  const handleToggleCollapse = useCallback((nodeId) => {
    const id = String(nodeId);
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const collapsedKey = useMemo(() => [...collapsedIds].sort().join(","), [collapsedIds]);

  const { nodes: initialNodes, edges: initialEdges, slotMap } = useMemo(
    () => slotsToFlow(slots, { collapsedIds, onToggleCollapse: handleToggleCollapse }),
    [slots, collapsedKey, handleToggleCollapse]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  useEffect(() => {
    if (viewportFitRef.current.userId !== userId) {
      viewportFitRef.current = { userId, didFit: false };
    }
  }, [userId]);

  useEffect(() => {
    if (initialNodes.length === 0) return;
    const activeId = simulatorActiveSlotId ? String(simulatorActiveSlotId) : null;
    setNodes(
      initialNodes.map((node) => ({
        ...node,
        data: {
          ...node.data,
          isSimulatorActive: activeId != null && node.id === activeId,
        },
      }))
    );
    setEdges(initialEdges);
    if (!viewportFitRef.current.didFit) {
      viewportFitRef.current.didFit = true;
      setTimeout(
        () =>
          fitView?.({
            padding: 0.12,
            minZoom: 0.4,
            maxZoom: 1.2,
          }),
        300
      );
    }
  }, [initialNodes, initialEdges, setNodes, setEdges, fitView, simulatorActiveSlotId]);

  useEffect(() => {
    const slotId = focusSlotRequest?.slotId;
    if (!slotId || !slots.length) return;

    const nodeId = String(slotId);
    if (!slotMap.has(nodeId)) return;

    const ancestors = getAncestorSlotIds(slotId, slots);
    if (ancestors.length) {
      setCollapsedIds((prev) => {
        const next = new Set(prev);
        ancestors.forEach((id) => next.delete(id));
        return next;
      });
    }

    setSelectedNodeId(nodeId);
    setSaveError(null);
    setIsPanelOpen(true);

    const timer = setTimeout(() => {
      fitView?.({
        nodes: [{ id: nodeId }],
        padding: 0.35,
        minZoom: 0.35,
        maxZoom: 1.2,
        duration: 280,
      });
    }, ancestors.length ? 380 : 120);

    return () => clearTimeout(timer);
  }, [focusSlotRequest?.ts, slots, slotMap, fitView]);

  useEffect(() => {
    const slotId = simulatorActiveSlotId;
    if (!slotId || !slots.length) {
      setNodes((nds) =>
        nds.map((n) => ({
          ...n,
          data: { ...n.data, isSimulatorActive: false },
        }))
      );
      return undefined;
    }

    const nodeId = String(slotId);
    if (!slotMap.has(nodeId)) return undefined;

    const ancestors = getAncestorSlotIds(slotId, slots);
    if (ancestors.length) {
      setCollapsedIds((prev) => {
        const next = new Set(prev);
        ancestors.forEach((id) => next.delete(id));
        return next;
      });
    }

    setNodes((nds) =>
      nds.map((n) => ({
        ...n,
        data: { ...n.data, isSimulatorActive: n.id === nodeId },
      }))
    );

    const timer = setTimeout(() => {
      fitView?.({
        nodes: [{ id: nodeId }],
        padding: 0.45,
        minZoom: 0.35,
        maxZoom: 1.2,
        duration: 320,
      });
    }, ancestors.length ? 380 : 120);

    return () => clearTimeout(timer);
  }, [simulatorActiveSlotId, slots, slotMap, fitView, setNodes]);

  const selectedSlot = selectedNodeId ? slotMap.get(selectedNodeId) ?? null : null;
  const isSelectedExcecao = selectedSlot?.identificador === "Exceção";

  const handleNodeClick = useCallback((_, node) => {
    setSelectedNodeId(node.id);
    setSaveError(null);
    setIsPanelOpen(true);
  }, []);

  const handleSave = useCallback(
    (payload) => {
      setSaveError(null);
      updateSlot.mutate(payload, {
        onSuccess: () => {
          const parentId = payload.id;
          const rawButtons = Array.isArray(payload.botoes)
            ? payload.botoes
            : String(payload.botoes || "")
                .split(",")
                .map((b) => b.trim())
                .filter(Boolean);

          const buttons = Array.from(new Set(rawButtons));
          const flowButtons = buttons.filter((btn) => !isLinkButton(btn));

          if (!flowButtons.length) {
            refetch();
            setIsPanelOpen(false);
            return;
          }

          const children = slots.filter((s) => Number(s.id_pai) === Number(parentId));
          const hasChildForButton = (btn) => {
            const flowKey = chatbotButtonFlowKey(btn);
            return children.some((child) => {
              const idStr = String(child.identificador || "");
              return idStr
                .split(",")
                .map((p) => p.trim())
                .filter(Boolean)
                .includes(flowKey);
            });
          };

          const missingButtons = flowButtons.filter((btn) => !hasChildForButton(btn));

          if (!missingButtons.length) {
            refetch();
            setIsPanelOpen(false);
            return;
          }

          let created = 0;
          const total = missingButtons.length;

          missingButtons.forEach((btn) => {
            createSlot.mutate(
              {
                user_id: userId,
                parent_id: parentId,
                identificador: chatbotButtonFlowKey(btn),
                criar_excecao: false,
                global: 0,
                ignorarglobal: 0,
              },
              {
                onSuccess: () => {
                  created += 1;
                  if (created === total) {
                    refetch();
                    setIsPanelOpen(false);
                  }
                },
                onError: (err) => {
                  setSaveError(err?.message || "Erro ao criar subpassos para os botões.");
                },
              }
            );
          });
        },
        onError: (err) => setSaveError(err?.message || "Erro ao salvar."),
      });
    },
    [updateSlot, refetch, slots, createSlot, userId]
  );

  const handleDelete = useCallback(
    (id) => {
      setSaveError(null);
      deleteSlot.mutate(id, {
        onSuccess: () => {
          refetch();
          setSelectedNodeId(null);
          setIsPanelOpen(false);
        },
        onError: (err) => setSaveError(err?.message || "Erro ao excluir."),
      });
    },
    [deleteSlot, refetch]
  );

  const handleAddException = useCallback(() => {
    if (!selectedNodeId || isSelectedExcecao) return;
    setSaveError(null);
    createSlot.mutate(
      {
        user_id: userId,
        parent_id: parseInt(selectedNodeId, 10),
        identificador: "Exceção",
        nome: "",
        resposta: "",
        criar_excecao: false,
        global: 0,
        ignorarglobal: 0,
      },
      {
        onSuccess: async (created) => {
          await refetch();
          const newId = created?.id != null ? String(created.id) : null;
          if (newId) setSelectedNodeId(newId);
          setIsPanelOpen(true);
        },
        onError: (err) => setSaveError(err?.message || "Erro ao criar exceção."),
      }
    );
  }, [selectedNodeId, isSelectedExcecao, createSlot, userId, refetch]);

  const canDeleteSelected = !!selectedSlot && canDeleteSlot(selectedSlot, slots);

  const canAddLocalException =
    !!selectedNodeId &&
    !isSelectedExcecao &&
    !hasLocalExceptionForParent(slots, selectedNodeId);

  const handleAddStep = useCallback(
    (asChild = false) => {
      setSaveError(null);
      const parentId = asChild && selectedNodeId ? parseInt(selectedNodeId, 10) : 0;
      if (asChild && selectedNodeId) {
        setCollapsedIds((prev) => {
          const next = new Set(prev);
          next.delete(String(selectedNodeId));
          return next;
        });
      }
      createSlot.mutate(
        {
          user_id: userId,
          parent_id: parentId,
          identificador: "",
          criar_excecao: false,
          global: 0,
          ignorarglobal: 0,
        },
        {
          onSuccess: async (created) => {
            await refetch();
            const newId = created?.id != null ? String(created.id) : null;
            if (newId) {
              setSelectedNodeId(newId);
              setIsPanelOpen(true);
            }
          },
          onError: (err) => setSaveError(err?.message || "Erro ao criar passo."),
        }
      );
    },
    [userId, selectedNodeId, createSlot, refetch]
  );

  if (!userId) return null;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  const hasSlots = slots.length > 0;
  const isLargeFlow = slots.length > LARGE_TREE_THRESHOLD;

  return (
    <Box sx={{ width: "100%", flex: 1, minHeight: 420, position: "relative" }}>
      {isLargeFlow && !largeFlowWarningDismissed ? (
        <Alert
          severity="warning"
          onClose={() => setLargeFlowWarningDismissed(true)}
          sx={{
            position: "absolute",
            top: 8,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 11,
            maxWidth: "min(520px, 92%)",
            py: 0.25,
          }}
        >
          Fluxo com {slots.length.toLocaleString("pt-BR")} passos — prefira a aba <strong>Árvore</strong> e a busca para editar.
        </Alert>
      ) : null}
      <Box
        sx={{
          position: "absolute",
          top: 12,
          left: 12,
          zIndex: 10,
          display: "flex",
          gap: 1.5,
          alignItems: "center",
          flexWrap: "wrap",
          p: 1.5,
          borderRadius: 2,
          bgcolor: "background.paper",
          boxShadow: 2,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        {!hasSlots && onEmptyInit ? (
          <Button variant="contained" color="primary" size="small" startIcon={<Add />} onClick={onEmptyInit}>
            Inicializar fluxo (Início + Exceção)
          </Button>
        ) : (
          <>
            <Tooltip title="Selecione um passo no canvas e clique aqui para criar um subpasso ligado a ele.">
              <span>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Add />}
                  onClick={() => handleAddStep(true)}
                  disabled={!selectedNodeId || isSelectedExcecao}
                >
                  Adicionar subpasso
                </Button>
              </span>
            </Tooltip>
            {canAddLocalException ? (
              <Tooltip title="Cria uma resposta de fallback só neste nível; sem isso, vale a exceção global do fluxo.">
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleAddException}
                    disabled={createSlot.isLoading}
                  >
                    Exceção neste nível
                  </Button>
                </span>
              </Tooltip>
            ) : null}
            {!selectedNodeId && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                Selecione um passo para adicionar subpasso
              </Typography>
            )}
            {selectedNodeId && isSelectedExcecao && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                O slot de Exceção não pode ter subpassos
              </Typography>
            )}
          </>
        )}
      </Box>

      {saveError && (
        <Alert severity="error" onClose={() => setSaveError(null)} sx={{ position: "absolute", top: 8, right: 8, zIndex: 10 }}>
          {saveError}
        </Alert>
      )}

      {hasSlots ? (
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={handleNodeClick}
          onlyRenderVisibleElements={isLargeFlow}
          proOptions={{ hideAttribution: true }}
          minZoom={0.15}
          maxZoom={1.5}
          defaultEdgeOptions={{
            type: "smoothstep",
            style: { strokeWidth: 2, stroke: "rgba(0,0,0,0.45)" },
            animated: false,
          }}
        >
          <Controls />
          {isLargeFlow ? null : <MiniMap />}
          <Background color="#e0e0e0" gap={16} />
        </ReactFlow>
      ) : (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
          bgcolor="action.hover"
          borderRadius={2}
        >
          <Typography color="text.secondary" gutterBottom>
            O fluxo tem um único passo raiz (Início). Inicialize para criar Início e Exceção; depois adicione subpassos a partir dos passos no canvas.
          </Typography>
          {onEmptyInit && (
            <Button variant="contained" startIcon={<Add />} onClick={onEmptyInit}>
              Inicializar fluxo (Início + Exceção)
            </Button>
          )}
        </Box>
      )}

      <ChatbotNodePanel
        slot={isPanelOpen ? selectedSlot : null}
        onClose={() => setIsPanelOpen(false)}
        onSave={handleSave}
        onDelete={handleDelete}
        onAddSubpasso={hasSlots && selectedNodeId && !isSelectedExcecao ? () => handleAddStep(true) : undefined}
        onAddException={canAddLocalException ? handleAddException : undefined}
        isSaving={updateSlot.isLoading}
        isDeleting={deleteSlot.isLoading}
        isAddingSubpasso={createSlot.isLoading}
        saveError={saveError}
        canDelete={canDeleteSelected}
        onNavigateToSlot={onNavigateToSlot}
      />
    </Box>
  );
}

export default function ChatbotFlowEditor({
  userId,
  onEmptyInit,
  focusSlotRequest,
  simulatorActiveSlotId,
  onNavigateToSlot,
}) {
  return (
    <ReactFlowProvider>
      <ChatbotFlowEditorInner
        userId={userId}
        onEmptyInit={onEmptyInit}
        focusSlotRequest={focusSlotRequest}
        simulatorActiveSlotId={simulatorActiveSlotId}
        onNavigateToSlot={onNavigateToSlot}
      />
    </ReactFlowProvider>
  );
}
